import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useGetRepDefaults,
  useUpdateRepDefaults,
  useResetCaseReferenceCursor,
  usePeekNextCaseReference,
  getGetRepDefaultsQueryKey,
  getPeekNextCaseReferenceQueryKey,
} from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Loader2,
  Save,
  IdCard,
  Wand2,
  PenLine,
  RotateCcw,
  AtSign,
} from "lucide-react";
import { toast } from "sonner";

type FormState = {
  repName: string;
  agentTitle: string;
  agentDepartment: string;
  repPhone: string;
  repEmail: string;
  employeeId: string;
  caseId: string;
  fromName: string;
  fromLocalPart: string;
};

const EMPTY_FORM: FormState = {
  repName: "",
  agentTitle: "",
  agentDepartment: "",
  repPhone: "",
  repEmail: "",
  employeeId: "",
  caseId: "",
  fromName: "",
  fromLocalPart: "",
};

// Same regex as lib/resend.ts on the server. Used for inline UI
// validation so the user gets a hint before submitting.
const LOCAL_PART_RE = /^[a-z0-9](?:[a-z0-9._-]{0,62}[a-z0-9])?$/;

export default function Representative() {
  const queryClient = useQueryClient();
  const { data, isLoading } = useGetRepDefaults();
  // Peek at what the next compose will consume so we can show a
  // "Next compose will use: …" hint beneath the Case ID input. The
  // endpoint is read-only — it intentionally does not advance the
  // cursor — so it's safe to refetch after every save.
  const { data: nextCaseRef } = usePeekNextCaseReference();
  const [form, setForm] = useState<FormState>(EMPTY_FORM);

  useEffect(() => {
    if (data) {
      setForm({
        repName: data.repName ?? "",
        agentTitle: data.agentTitle ?? "",
        agentDepartment: data.agentDepartment ?? "",
        repPhone: data.repPhone ?? "",
        repEmail: data.repEmail ?? "",
        employeeId: data.employeeId ?? "",
        caseId: data.caseId ?? "",
        fromName: data.fromName ?? "",
        fromLocalPart: data.fromLocalPart ?? "",
      });
    }
  }, [data]);

  const updateMutation = useUpdateRepDefaults();
  const resetCursorMutation = useResetCaseReferenceCursor();
  const [confirmResetOpen, setConfirmResetOpen] = useState(false);
  // When the user clicks Save and the trimmed Case ID differs from
  // the previously-saved value, the server resets the per-user
  // case-reference cursor as a side effect. We surface that with a
  // confirm dialog so an accidental edit can be caught before the
  // counter jumps. The prompt fires for every trimmed change —
  // including empty -> value — to mirror the server's reset trigger
  // exactly.
  const [confirmCaseIdChangeOpen, setConfirmCaseIdChangeOpen] = useState(false);

  // Disable the reset action when no Case ID is currently saved on the
  // server — the cursor only exists once the rep has seeded one, so
  // there is nothing to reset back to. We intentionally check the
  // *server-saved* value (`data.caseId`) rather than the unsaved form
  // field, since the cursor seed is whatever was last persisted.
  const savedCaseId = (data?.caseId ?? "").trim();
  const canResetCursor = savedCaseId.length > 0;

  async function handleResetCursor() {
    try {
      await resetCursorMutation.mutateAsync();
      toast.success(`Case reference counter reset to ${savedCaseId}`);
      queryClient.invalidateQueries({
        queryKey: getPeekNextCaseReferenceQueryKey(),
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to reset";
      toast.error(msg);
    } finally {
      setConfirmResetOpen(false);
    }
  }

  async function performSave() {
    const trimmedLocal = form.fromLocalPart.trim().toLowerCase();
    try {
      await updateMutation.mutateAsync({
        data: {
          repName: form.repName.trim() || null,
          agentTitle: form.agentTitle.trim() || null,
          agentDepartment: form.agentDepartment.trim() || null,
          repPhone: form.repPhone.trim() || null,
          repEmail: form.repEmail.trim() || null,
          employeeId: form.employeeId.trim() || null,
          caseId: form.caseId.trim() || null,
          fromName: form.fromName.trim() || null,
          fromLocalPart: trimmedLocal || null,
        },
      });
      toast.success("Representative defaults saved");
      queryClient.invalidateQueries({ queryKey: getGetRepDefaultsQueryKey() });
      // The PUT may reset the per-user cursor (when the Case ID
      // changed), so the peek result is now stale — refetch it so
      // the "Next compose will use: …" hint reflects the new seed.
      queryClient.invalidateQueries({
        queryKey: getPeekNextCaseReferenceQueryKey(),
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to save";
      toast.error(msg);
    }
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const trimmedLocal = form.fromLocalPart.trim().toLowerCase();
    if (trimmedLocal && !LOCAL_PART_RE.test(trimmedLocal)) {
      toast.error(
        "From address must be 1-64 lowercase characters using only letters, digits, and . _ -",
      );
      return;
    }
    // Intercept Case ID changes that would silently reset the
    // per-user case-reference cursor. The server resets the cursor
    // whenever the trimmed value differs from what was previously
    // saved (see artifacts/api-server/src/routes/repDefaults.ts), so
    // we mirror that exact comparison here for the prompt.
    const nextCaseId = form.caseId.trim();
    if (nextCaseId !== savedCaseId) {
      setConfirmCaseIdChangeOpen(true);
      return;
    }
    await performSave();
  }

  async function handleConfirmCaseIdChange() {
    setConfirmCaseIdChangeOpen(false);
    await performSave();
  }

  if (isLoading) {
    return (
      <div className="flex-1 overflow-auto p-8 bg-background">
        <div className="max-w-3xl mx-auto py-20 flex justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-auto p-8 bg-background">
      <div className="max-w-3xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <IdCard className="w-6 h-6 text-primary" />
            Representative Defaults
          </h1>
          <p className="text-muted-foreground mt-1">
            Saved here once, auto-filled into every email — so you only need to
            change the customer's name, email or phone per send.
          </p>
        </div>

        <form onSubmit={submit}>
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Your details</CardTitle>
              <CardDescription>
                Used to fill template variables named{" "}
                <code className="text-xs bg-muted px-1 py-0.5 rounded">
                  agentName
                </code>
                ,{" "}
                <code className="text-xs bg-muted px-1 py-0.5 rounded">
                  agentTitle
                </code>
                ,{" "}
                <code className="text-xs bg-muted px-1 py-0.5 rounded">
                  agentDepartment
                </code>
                ,{" "}
                <code className="text-xs bg-muted px-1 py-0.5 rounded">
                  repName
                </code>
                ,{" "}
                <code className="text-xs bg-muted px-1 py-0.5 rounded">
                  repPhone
                </code>
                ,{" "}
                <code className="text-xs bg-muted px-1 py-0.5 rounded">
                  repEmail
                </code>
                ,{" "}
                <code className="text-xs bg-muted px-1 py-0.5 rounded">
                  employeeId
                </code>{" "}
                and{" "}
                <code className="text-xs bg-muted px-1 py-0.5 rounded">
                  caseId
                </code>{" "}
                whenever a template uses them.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="rep-name">Agent name</Label>
                  <Input
                    id="rep-name"
                    value={form.repName}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, repName: e.target.value }))
                    }
                    placeholder="Jane Doe"
                    data-testid="input-rep-name"
                  />
                  <p className="text-[11px] text-muted-foreground">
                    Auto-fills{" "}
                    <code className="text-[10px] bg-muted px-1 py-0.5 rounded">
                      {`{{agentName}}`}
                    </code>{" "}
                    and{" "}
                    <code className="text-[10px] bg-muted px-1 py-0.5 rounded">
                      {`{{repName}}`}
                    </code>{" "}
                    in templates. Saving a new value also re-renders your
                    handwritten signature image.
                  </p>
                  {data?.handwrittenSignatureUrl ? (
                    <div className="mt-2 rounded border bg-white p-2 inline-flex">
                      <img
                        src={`${data.handwrittenSignatureUrl}?v=${encodeURIComponent(
                          new Date(data.updatedAt ?? Date.now()).getTime(),
                        )}`}
                        alt={`Signature preview of ${form.repName || "your name"}`}
                        className="block"
                        style={{
                          maxHeight: 36,
                          height: "auto",
                          width: "auto",
                          background: "#fff",
                        }}
                        data-testid="img-signature-inline-preview"
                      />
                    </div>
                  ) : null}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="agent-title">Designation (Agent title)</Label>
                  <Input
                    id="agent-title"
                    value={form.agentTitle}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, agentTitle: e.target.value }))
                    }
                    placeholder="Senior Trust & Safety Specialist"
                    data-testid="input-agent-title"
                  />
                  <p className="text-[11px] text-muted-foreground">
                    Your job title or role label as it should appear in the
                    sign-off — e.g. "Senior Trust &amp; Safety Specialist" or
                    "Customer Success Manager". Auto-fills{" "}
                    <code className="text-[10px] bg-muted px-1 py-0.5 rounded">
                      {`{{agentTitle}}`}
                    </code>{" "}
                    in templates.
                  </p>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="agent-department">
                    Department / Team
                  </Label>
                  <Input
                    id="agent-department"
                    value={form.agentDepartment}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        agentDepartment: e.target.value,
                      }))
                    }
                    placeholder="Technical Support Team"
                    data-testid="input-agent-department"
                  />
                  <p className="text-[11px] text-muted-foreground">
                    The team line that appears beneath your title in the
                    sign-off — e.g. "Technical Support Team" or "Trust &amp;
                    Safety Team". Auto-fills{" "}
                    <code className="text-[10px] bg-muted px-1 py-0.5 rounded">
                      {`{{agentDepartment}}`}
                    </code>{" "}
                    in templates.
                  </p>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="rep-phone">Phone Number</Label>
                  <Input
                    id="rep-phone"
                    value={form.repPhone}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, repPhone: e.target.value }))
                    }
                    placeholder="+1 555 0100"
                    data-testid="input-rep-phone"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="rep-email">Representative Email</Label>
                  <Input
                    id="rep-email"
                    type="email"
                    value={form.repEmail}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, repEmail: e.target.value }))
                    }
                    placeholder="jane.doe@company.com"
                    data-testid="input-rep-email"
                  />
                  <p className="text-[11px] text-muted-foreground">
                    Auto-fills{" "}
                    <code className="text-[10px] bg-muted px-1 py-0.5 rounded">
                      {`{{repEmail}}`}
                    </code>{" "}
                    and{" "}
                    <code className="text-[10px] bg-muted px-1 py-0.5 rounded">
                      {`{{agentEmail}}`}
                    </code>{" "}
                    in templates that include a "reach me at" line, and
                    is shown alongside your handwritten signature in
                    outgoing mail.
                  </p>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="rep-employee-id">Employee ID</Label>
                  <Input
                    id="rep-employee-id"
                    value={form.employeeId}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, employeeId: e.target.value }))
                    }
                    placeholder="EMP-12345"
                    data-testid="input-rep-employee-id"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="rep-case-id">Case ID (starting serial)</Label>
                  <Input
                    id="rep-case-id"
                    value={form.caseId}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, caseId: e.target.value }))
                    }
                    placeholder="CASE-2026-0001"
                    data-testid="input-rep-case-id"
                  />
                  <p className="text-xs text-muted-foreground">
                    Used as the starting serial number for the case
                    reference on every email. The trailing digits
                    auto-advance by 1 on every fresh compose — e.g.
                    saving <code>CASE-2026-0001</code> gives you{" "}
                    <code>CASE-2026-0001</code> on the next compose,
                    then <code>CASE-2026-0002</code>, then{" "}
                    <code>CASE-2026-0003</code>, and so on. Save a new
                    value any time to restart the count from there.
                  </p>
                  {nextCaseRef?.nextCaseReference ? (
                    <p
                      className="text-xs text-foreground/80"
                      data-testid="text-next-case-reference"
                    >
                      Next compose will use:{" "}
                      <code
                        className="text-[11px] bg-muted px-1 py-0.5 rounded font-medium"
                        data-testid="text-next-case-reference-value"
                      >
                        {nextCaseRef.nextCaseReference}
                      </code>
                    </p>
                  ) : null}
                  <div className="pt-1">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={
                        !canResetCursor || resetCursorMutation.isPending
                      }
                      onClick={() => setConfirmResetOpen(true)}
                      data-testid="button-reset-case-reference-cursor"
                    >
                      {resetCursorMutation.isPending ? (
                        <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />
                      ) : (
                        <RotateCcw className="w-3.5 h-3.5 mr-2" />
                      )}
                      Reset to saved Case ID
                    </Button>
                    {!canResetCursor ? (
                      <p className="text-[11px] text-muted-foreground mt-1">
                        Save a Case ID first to enable this.
                      </p>
                    ) : null}
                  </div>
                </div>
              </div>

              <div className="rounded-md border border-border bg-muted/30 px-3 py-2.5 text-xs text-muted-foreground flex items-start gap-2">
                <Wand2 className="w-3.5 h-3.5 shrink-0 mt-0.5 text-primary" />
                <div>
                  These values are also pre-filled in the Compose page whenever
                  you pick a template that uses them — you can still override
                  any field per send.
                </div>
              </div>
            </CardContent>
          </Card>

          {/*
            Signature preview card. Shows the auto-generated handwritten
            signature PNG once the user has saved a representative name.
            The image is regenerated server-side any time `repName` is
            saved with a different value, so the cache-busting key uses
            `updatedAt` rather than the URL itself.
          */}
          <Card className="mt-4">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <PenLine className="w-5 h-5 text-primary" />
                Handwritten signature
              </CardTitle>
              <CardDescription>
                Auto-generated cursive signature based on your saved Agent
                name. Embedded in every email below your printed name via the{" "}
                <code className="text-xs bg-muted px-1 py-0.5 rounded">
                  {`{{handwrittenSignature}}`}
                </code>{" "}
                template variable. Re-generated whenever you save a new name.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {data?.handwrittenSignatureUrl ? (
                <div
                  className="rounded-md border border-border bg-white p-3 inline-block"
                  data-testid="signature-preview-wrapper"
                >
                  <img
                    src={`${data.handwrittenSignatureUrl}?v=${encodeURIComponent(
                      data.updatedAt ?? "",
                    )}`}
                    alt="Your handwritten signature preview"
                    className="block max-h-[110px] w-auto"
                    data-testid="signature-preview-img"
                  />
                </div>
              ) : (
                <p
                  className="text-sm text-muted-foreground"
                  data-testid="signature-preview-empty"
                >
                  Save an Agent name above and your signature will appear here.
                </p>
              )}
            </CardContent>
          </Card>

          {/*
            Per-user "From" address card. Lets every user pick the
            display name and local-part used in the From header on
            outbound sends. The domain is pinned to the workspace's
            verified shared sending domain (read-only) — exposed by
            the API as `data.fromDomain` so we never have to hard-code
            it on the client.
          */}
          <Card className="mt-4">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <AtSign className="w-5 h-5 text-primary" />
                From address
              </CardTitle>
              <CardDescription>
                Pick the display name and the part of the email
                address before{" "}
                <code className="text-xs bg-muted px-1 py-0.5 rounded">
                  @{data?.fromDomain ?? ""}
                </code>{" "}
                that recipients will see in their inbox. Only your own
                sends are affected — this doesn't change the workspace
                defaults for anyone else.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="from-name">From name</Label>
                  <Input
                    id="from-name"
                    value={form.fromName}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, fromName: e.target.value }))
                    }
                    placeholder="Kevin from Cloud AI"
                    maxLength={200}
                    data-testid="input-from-name"
                  />
                  <p className="text-[11px] text-muted-foreground">
                    The friendly display name shown in the recipient's
                    inbox (e.g. "Kevin from Cloud AI"). Leave blank to
                    fall back to the workspace default.
                  </p>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="from-local-part">From address</Label>
                  <div className="flex items-stretch">
                    <Input
                      id="from-local-part"
                      value={form.fromLocalPart}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          fromLocalPart: e.target.value
                            .toLowerCase()
                            .replace(/[^a-z0-9._-]/g, ""),
                        }))
                      }
                      placeholder="kevin"
                      maxLength={64}
                      autoComplete="off"
                      spellCheck={false}
                      className="rounded-r-none"
                      data-testid="input-from-local-part"
                    />
                    <span
                      className="inline-flex items-center px-3 rounded-r-md border border-l-0 border-input bg-muted text-sm text-muted-foreground select-all"
                      data-testid="from-domain-suffix"
                    >
                      @{data?.fromDomain ?? ""}
                    </span>
                  </div>
                  <p className="text-[11px] text-muted-foreground">
                    Lowercase letters, digits, and{" "}
                    <code className="text-[10px] bg-muted px-1 py-0.5 rounded">
                      . _ -
                    </code>{" "}
                    only (e.g. "kevin", "kevin.h", "support-2"). Leave
                    blank to fall back to the workspace default. The
                    domain after the @ is fixed.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="mt-4 flex justify-end">
            <Button
              type="submit"
              disabled={updateMutation.isPending}
              data-testid="button-save-rep-defaults"
            >
              {updateMutation.isPending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save defaults
            </Button>
          </div>
        </form>

        <AlertDialog
          open={confirmResetOpen}
          onOpenChange={setConfirmResetOpen}
        >
          <AlertDialogContent data-testid="dialog-reset-case-reference">
            <AlertDialogHeader>
              <AlertDialogTitle>Reset case reference counter?</AlertDialogTitle>
              <AlertDialogDescription>
                The next compose will start from your saved Case ID
                {savedCaseId ? (
                  <>
                    {" "}
                    (<code>{savedCaseId}</code>)
                  </>
                ) : null}{" "}
                again. Any numbers already handed out to drafts you didn't
                send will be re-issued.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel
                data-testid="button-cancel-reset-case-reference"
                disabled={resetCursorMutation.isPending}
              >
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={(e) => {
                  e.preventDefault();
                  void handleResetCursor();
                }}
                disabled={resetCursorMutation.isPending}
                data-testid="button-confirm-reset-case-reference"
              >
                {resetCursorMutation.isPending ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <RotateCcw className="w-4 h-4 mr-2" />
                )}
                Reset counter
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AlertDialog
          open={confirmCaseIdChangeOpen}
          onOpenChange={(open) => {
            // Don't let the dialog close while the save is mid-flight,
            // so the user can't double-fire it via the overlay click.
            if (!open && updateMutation.isPending) return;
            setConfirmCaseIdChangeOpen(open);
          }}
        >
          <AlertDialogContent data-testid="dialog-confirm-case-id-change">
            <AlertDialogHeader>
              <AlertDialogTitle>
                Save and reset case reference counter?
              </AlertDialogTitle>
              <AlertDialogDescription>
                You're changing your Case ID from{" "}
                <code data-testid="text-old-case-id">
                  {savedCaseId || "(empty)"}
                </code>{" "}
                to{" "}
                <code data-testid="text-new-case-id">
                  {form.caseId.trim() || "(empty)"}
                </code>
                . Saving will reset the case-reference counter so the next
                compose starts from the new value. Any numbers already
                handed out to drafts you didn't send will be re-issued.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel
                data-testid="button-cancel-case-id-change"
                disabled={updateMutation.isPending}
              >
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={(e) => {
                  e.preventDefault();
                  void handleConfirmCaseIdChange();
                }}
                disabled={updateMutation.isPending}
                data-testid="button-confirm-case-id-change"
              >
                {updateMutation.isPending ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Save and reset counter
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}
